import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { JWTTokenService } from './jwt-token.service';
import { AuthStateService, UserInfo } from './auth-state.service';

interface LoginResponse {
  // Backend uses httpOnly cookies for JWT - no token in response
  // Response contains user info for localStorage
  success?: boolean;
  id?: number;
  username?: string;
  fullname?: string;
  picture?: string | null;
  roles?: string[];
  expiresAt?: number;
  totp_required?: boolean;
  // Allow any other properties from backend
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private readonly http: HttpClient,
    private readonly jwtService: JWTTokenService,
    private readonly router: Router,
    private readonly authState: AuthStateService,
  ) {}

  login(email: string, password: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(
        '/login',
        { email, password },
        {
          // CRITICAL: withCredentials allows cookies to be sent/received
          withCredentials: true,
        },
      )
      .pipe(
        tap((response) => {
          // Backend sets httpOnly cookie automatically via JwtCookieEventSubscriber
          // Store user info using AuthStateService
          this.authState.setAuthenticated(true);

          // Store user info excluding success flag (destruct to omit)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { success, expiresAt, ...userInfo } = response;
          this.authState.setUserInfo(userInfo as UserInfo);

          if (typeof expiresAt === 'number') {
            this.authState.setExpiration(expiresAt);
          }
        }),
      );
  }

  verifyTotp(code: string): Observable<{ verified: boolean }> {
    return this.http.post<{ verified: boolean }>('/totp/verify', { code });
  }

  askNewPassword(email: string): Observable<unknown> {
    return this.http.post<unknown>('/api/password-resets', {
      email: email.trim(),
    });
  }

  logout(): void {
    // Call backend logout to clear cookies
    this.http.post('/logout', {}, { withCredentials: true }).subscribe({
      next: () => {
        this.jwtService.setToken(null);
        this.authState.clearAuthState();
        this.router.navigate(['/login']);
      },
      error: () => {
        // Even if logout fails, clear local state
        this.jwtService.setToken(null);
        this.authState.clearAuthState();
        this.router.navigate(['/login']);
      },
    });
  }
}
